import { useState, useCallback, useRef } from 'react';
import { authenticatedFetch } from '@diggens/api-client';
import type { PageContext, PlanStreamEvent, RemovalCard } from '@diggens/types';

interface StreamMessage {
  type: 'token' | 'actions' | 'done' | 'error' | 'replace' | 'plan' | 'removal_card';
  content?: string; // For 'token' and 'replace' types
  actions?: any[]; // For 'actions' type
  conversation_id?: string; // For 'done' type
  message?: string; // For 'error' type
  event?: PlanStreamEvent; // For 'plan' type
  card?: RemovalCard; // For 'removal_card' type
}

interface UseChatStreamOptions {
  onToken: (token: string) => void;
  onReplace?: (content: string) => void; // Replace all streamed text (used when handoff agent returns JSON)
  onActions: (actions: any[]) => void;
  onComplete: (conversationId: string) => void;
  onError: (error: string) => void;
  onPlanEvent?: (event: PlanStreamEvent) => void; // The Manager proposed a plan, it was decided, or a step moved on
  onRemovalCard?: (card: RemovalCard) => void; // The reply carries a card asking the person to remove a preference
  onDetachedComplete?: (conversationId: string) => void; // A reply the screen left behind finished, and the server saved it
}

export function useChatStream({ onToken, onReplace, onActions, onComplete, onError, onPlanEvent, onRemovalCard, onDetachedComplete }: UseChatStreamOptions) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  // The stream the screen shows; an earlier one keeps reading, so the server's save is seen, but reaches nothing on screen
  const attachedStreamRef = useRef(0);

  const sendMessage = useCallback(
    async (payload: {
      message: string;
      tenant_id: string;
      user_id: string;
      user_name: string;
      conversation_id?: string;
      fileMetadata?: any;
      /** Where in the apps the user is, so the Chief of Staff can resolve "this record"; the apps panel sends it. */
      page_context?: PageContext;
      /** Only for a message the person typed in the chat box; only such a message can teach the Chief of Staff a preference. */
      typed?: true;
    }) => {
      const streamId = ++attachedStreamRef.current;
      const attached = () => attachedStreamRef.current === streamId;
      // Create abort controller for cancellation
      const controller = new AbortController();
      setAbortController(controller);
      setIsStreaming(true);

      try {
        const response = await authenticatedFetch('/chat', {
          method: 'POST',
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        // Read SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages (separated by \n\n)
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || ''; // Keep incomplete message in buffer

          for (const message of messages) {
            if (!message.trim() || !message.startsWith('data: ')) {
              continue;
            }

            try {
              // Parse SSE message
              const jsonString = message.replace(/^data: /, '');
              const data = JSON.parse(jsonString) as StreamMessage;

              // A detached reply only reports that it was saved; its errors are logged, since nobody is watching it
              if (!attached()) {
                if (data.type === 'done' && data.conversation_id) onDetachedComplete?.(data.conversation_id);
                if (data.type === 'error') console.warn('Detached chat stream reported an error:', data.message);
                continue;
              }

              // Handle different event types
              switch (data.type) {
                case 'token':
                  if (data.content) {
                    onToken(data.content);
                  }
                  break;

                case 'replace':
                  if (data.content && onReplace) {
                    onReplace(data.content);
                  }
                  break;

                case 'actions':
                  if (data.actions) {
                    onActions(data.actions);
                  }
                  break;

                case 'plan':
                  if (data.event && onPlanEvent) {
                    onPlanEvent(data.event);
                  }
                  break;

                case 'removal_card':
                  if (data.card && onRemovalCard) {
                    onRemovalCard(data.card);
                  }
                  break;

                case 'done':
                  if (data.conversation_id) {
                    onComplete(data.conversation_id);
                  }
                  break;

                case 'error':
                  onError(data.message || 'Unknown error');
                  break;
              }
            } catch (parseError) {
              console.error('Failed to parse SSE message:', message, parseError);
            }
          }
        }
      } catch (error) {
        if (!attached()) {
          console.warn('Detached chat stream failed:', error);
          return;
        }
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream aborted by user');
        } else {
          onError(error instanceof Error ? error.message : 'Stream failed');
        }
      } finally {
        if (attached()) {
          setIsStreaming(false);
          setAbortController(null);
        }
      }
    },
    [onToken, onActions, onComplete, onError, onPlanEvent, onRemovalCard, onDetachedComplete]
  );

  /** Lets the reply streaming now go on without the screen: its text is ignored, and its save is still reported. */
  const detachStream = useCallback(() => {
    attachedStreamRef.current += 1;
    // Nothing is streaming to the screen any more, and the detached reply is not this hook's to cancel
    setIsStreaming(false);
    setAbortController(null);
  }, []);

  const cancelStream = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
  }, [abortController]);

  return {
    sendMessage,
    isStreaming,
    cancelStream,
    detachStream,
  };
}
